// --- React ---
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// --- Services ---
import { useUnsavedChangesWarning } from "../../../hooks/useUnsavedChangesWarning";
import problemSvc from "../../../services/api/problem";

// --- Components ---
import ModalConfirm from "../../../components/modal-comfirm";
import Loading from "../../../components/loading/loading";
import FormInput from "../../../components/form-input/FormInput";
import { notify } from "../../../common/notificationService";
import { omitMetaFields } from "../../../utils/helper";
import ButtonGoback from "../../../components/button/buttonGoback";

// --- Models ---
import { problemSchema } from "../../../models/schema/problem";
import { ProblemItem } from "../../../models/types/problem";

// --- Utils ---
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { LoaderCircle } from "lucide-react";
import { UrlCollection } from "../../../common/url-collection";
import FormSelect from "../../../components/form-input/formSelect";
import { useLookupStation, useLookupProblemStatus } from "../../../utils/get-lookup";
import FormTextarea from "../../../components/form-input/FormTextarea";
import FormDate from "../../../components/form-input/formDate";
import FormDropzone from "../../../components/form-input/FormDropzone";
import { FileItem } from "../../../models/types/common";
import { listProblemType } from "../../../utils/helper";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

interface FormProps {
  isCreate: boolean;
  data: ProblemItem | null;
}

export default function ProblemCreateUpdate({ isCreate, data }: FormProps) {
  const navigate = useNavigate();
  const { t } = useTranslation()
  const today = dayjs();
  const { lookupStation } = useLookupStation();
  const { lookupProblemStatus } = useLookupProblemStatus();

  const [loading, setLoading] = useState(false);

  const schema = problemSchema;

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      files: [],
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        ...omitMetaFields(data, ['files']),
        files: data.files && data.files.length > 0 ? data.files.map(f => Number(f.id)) : [],
      });
    }
  }, [data]);

  const onSubmit = async (values: any) => {
    setLoading(true);
    try {
      const res = isCreate ? await problemSvc.createProblem(values) : await problemSvc.updateProblem({ ...values, id: Number(data?.id) });
      if (res && res.content.status) {
        unlock();
        notify(t("common.success"), isCreate ? t("problem.notifications.createSuccess") : t("problem.notifications.updateSuccess"), "success");
        setLoading(false);
        navigate(UrlCollection.Problem);
      }
    } catch (e: any) {
      notify(t("common.error"), t("common.errorOccurred"), "danger");
    } finally {
      setLoading(false);
    }
  };

  const { showModal, confirmLeave, cancelLeave, confirmContent, unlock } = useUnsavedChangesWarning(isDirty);

  return (
    <>
      <ModalConfirm
        isOpen={showModal}
        onClose={cancelLeave}
        title={confirmContent.title}
        content={confirmContent.content}
        titleComfirm={t("common.leave")}
        action={confirmLeave}
      />
      <div className="w-full h-full">
        <ButtonGoback />
        <div className="w-full mx-auto p-8 bg-white rounded-lg shadow mt-3">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
              <FormSelect
                disabled={Boolean(data)}
                label={t("problem.form.fields.station")}
                name="stationId"
                control={control}
                options={lookupStation}
                errors={errors.stationId}
                required
              />
              <FormSelect
                disabled={Boolean(data)}
                label={t("problem.form.fields.problemType")}
                name="problemType"
                control={control}
                options={listProblemType}
                errors={errors.problemType}
                required
              />
              <FormSelect
                label={t("problem.form.fields.status")}
                name="status"
                control={control}
                options={lookupProblemStatus}
                errors={errors.status}
                required
              />
              <FormDate
                disabled={Boolean(data)}
                label={t("problem.form.fields.startDate")}
                name="start"
                control={control}
                errors={errors}
                outputFormat="YYYY-MM-DD"
                maxDate={dayjs(watch('end'))}
                required
              />
              <FormDate
                disabled={Boolean(data)}
                label={t("problem.form.fields.endDate")}
                name="end"
                control={control}
                outputFormat="YYYY-MM-DD"
                errors={errors}
                minDate={dayjs(watch('start'))}
              />
            </div>
            <div className="mt-4
            ">
              <FormTextarea
                disabled={Boolean(data)}
                label={t("problem.form.fields.description")}
                name="description"
                register={register("description")}
                error={errors.description}
                rows={3}
              />
            </div>
            <div className="mt-4">
              <FormDropzone
                disabled={Boolean(data)}
                setValue={(files: FileItem[]) => setValue('files', files.map(f => Number(f.id)), { shouldDirty: true })}
                label="Tệp đính kèm"
                multiple
                accept={{
                  'application/pdf': ['.pdf'],
                  'application/msword': ['.doc'],
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                  'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
                }}
                maxSize={10 * 1024 * 1024} // 10MB per file
                helperText="Upload PDF or Word documents (max 10MB each)."
                defaultValue={[]}
              />
            </div>
            <button
              disabled={!isDirty}
              type="submit"
              className="btn_submit mt-6"
            >
              <div className="flex items-center justify-center gap-2">
                {loading ? <LoaderCircle /> : ""}
                {isCreate ? t("problem.form.buttons.create") : t("problem.form.buttons.update")}
              </div>
            </button>
          </form>
        </div>
      </div>
    </>
  );
}